import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from '@aimsos/i18n';

import { getMemberResources, removeMemberResourceAccess, validateApiResponse } from '../../../services';
import { MEMBER_STATUS } from '../constants/copy';
import { useMemberTabData } from './useMemberTabData';
import { useMemberTabMutation } from './useMemberTabMutation';
import { useToastRef } from './useToastRef';

const mapResource = (raw) => ({
  id: raw.id,
  name: raw.name,
  type: raw.type,
  access: raw.access,
  source: raw.source,
  via: raw.via ?? null,
  grantedBy: raw.granted_by ?? '',
  grantedAt: raw.granted_at ?? null,
  otherPaths: raw.other_paths ?? 0,
  isDirect: raw.source === 'direct',
});

/**
 * Resources a member reaches: `{ resources, isLoading, error, removedAny }` plus
 * `removeAccess(resource)`, which removes the DIRECT access, toasts "Access removed" and refetches
 * (resolves `true`). `removedAny` tells an empty list that was emptied here ("No resources
 * remaining") from one that was always empty.
 *
 * @param {{ id: string, displayName: string, status: string }} member UI member
 */
export const useMemberResources = (member) => {
  const { t } = useTranslation('people-access');
  const toastRef = useToastRef();
  const [removedAny, setRemovedAny] = useState(false);
  const memberId = member.id;

  const load = useCallback(async () => {
    const res = validateApiResponse(await getMemberResources(memberId), t('memberTabs.loadError'));
    return res.body.items.map(mapResource);
  }, [memberId, t]);
  const { data, isLoading, error, refresh, retry } = useMemberTabData(memberId, load, {
    enabled: member.status !== MEMBER_STATUS.REMOVED,
  });

  const { notifyRefreshFailed } = useMemberTabMutation({
    refresh,
    failedTitleKey: 'memberTabs.resources.toast.failedTitle',
    toastRef,
  });

  const removeAccess = useCallback(
    async (resource) => {
      try {
        validateApiResponse(
          await removeMemberResourceAccess(memberId, resource.id),
          t('memberTabs.resources.toast.failedTitle'),
        );
        setRemovedAny(true);
        const fresh = await refresh();
        toastRef.current.success({
          title: t('memberTabs.resources.toast.removedTitle'),
          message: t('memberTabs.resources.toast.removedMessage', {
            name: member.displayName,
            resource: resource.name,
          }),
        });
        if (fresh === false) notifyRefreshFailed();
        return true;
      } catch (err) {
        toastRef.current.error({
          title: t('memberTabs.resources.toast.failedTitle'),
          message: err?.message ?? t('memberDetail.toast.unknown'),
        });
        return false;
      }
    },
    [memberId, member.displayName, refresh, notifyRefreshFailed, toastRef, t],
  );

  return useMemo(
    () => ({ resources: data ?? [], isLoading, error, removedAny, removeAccess, reload: retry }),
    [data, isLoading, error, removedAny, removeAccess, retry],
  );
};
